import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { money } from '../../utils/money';
import { VENUES } from '../../constants/meetingOptions';
import { weekLabel, shiftMonth, monthTitle, isWon } from './metrics';
import './ClosedDeals.css';

// Closed deals for one salesperson: what they won and lost this month, each deal
// once, at the week it closed. The weekly carry leaves won AND lost deals behind
// (backend/services/meetingScheduler.js), so after the week a deal closes this panel
// is the only place it appears. Collapsed by default; the counts on the tabs are
// always visible so "2 won, 1 lost" needs no click.
//
// The figures are the server's (GET /api/meetings/closed-deals, one call per month
// shown). Nothing is derived from the week's table here except one thing: whether a
// lost client is ALREADY back on this week's table, so Reopen is not offered twice.
//
// Reopen is an ordinary add-deal POST -- the same route the table's Add uses, with the
// same permission -- so there is no second write path to secure. The copy opens as a
// Quotation with the closing week noted in its details.

const API_BASE = '/api';
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const LOST_STATUS = 'Lost Deal';

const venueLabel = (v) => { const m = VENUES.find(x => x.code === v); return m ? m.label : v; };
const norm = (s) => String(s ?? '').trim().toLowerCase().replace(/\s+/g, ' ');
const isClosed = (d) => isWon(d) || (Array.isArray(d?.statuses) && d.statuses.includes(LOST_STATUS));
const EMPTY = { won: [], lost: [] };

// `month` is the meeting's month key ('2026-09') or its date ('2026-09-11'): both
// reduce to the month. A missing value falls back to today's month rather than a
// request for month 'undefined'.
const monthKeyOf = (month) => {
  const m = /^(\d{4}-\d{2})/.exec(String(month ?? ''));
  return m ? m[1] : new Date().toISOString().slice(0, 7);
};

export default function ClosedDeals({ meetingId, salespersonId, month, editable, deals, onReopened }) {
  const start = monthKeyOf(month);
  const [cur, setCur] = useState(start);
  const [tab, setTab] = useState('won');
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(EMPTY);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(null);

  // Follow the meeting: switching weeks into another month resets the panel to it.
  useEffect(() => { setCur(start); }, [start]);

  // Refetch when the month changes AND when this week's deals change: marking a deal
  // Lost on the table above should move the Lost count here without a reload.
  useEffect(() => {
    let alive = true;
    setError('');
    axios.get(`${API_BASE}/meetings/closed-deals`, { params: { month: cur }, headers: headers() })
      .then(r => { if (alive) setData(r.data?.people?.[salespersonId] || EMPTY); })
      .catch(() => { if (alive) { setData(EMPTY); setError('Could not load closed deals'); } });
    return () => { alive = false; };
  }, [cur, salespersonId, deals]);

  const list = data[tab] || [];
  // Clients already on this week's table as an OPEN deal: Reopen would only add a twin.
  const openNow = new Set((deals || []).filter(d => !isClosed(d)).map(d => norm(d.client_name)));

  const reopen = async (d) => {
    setBusy(d.id); setError('');
    const note = `Reopened from ${weekLabel(d.closed_on)}`;
    try {
      await axios.post(`${API_BASE}/meetings/${meetingId}/sections/${salespersonId}/deals`, {
        client_name: d.client_name,
        event_month: d.event_month,
        ticket_size: d.ticket_size,
        winning_chance: d.winning_chance || 'So-So',
        statuses: ['Quotation'],
        deal_details: d.deal_details ? `${note} · ${d.deal_details}` : note,
        venues: d.venues || [],
      }, { headers: headers() });
      if (onReopened) onReopened();
    } catch (e) {
      setError(e.response?.data?.error || 'Reopen failed');
    } finally {
      setBusy(null);
    }
  };

  const pickTab = (t) => { setTab(t); setOpen(true); };
  const showReopen = editable && tab === 'lost';

  return (
    <div className={'ml-closed' + (open ? ' is-open' : '')} data-testid="closed-deals">
      <div className="ml-closed-head">
        <button type="button" className="ml-closed-toggle" aria-expanded={open}
                onClick={() => setOpen(o => !o)}>
          <span className="ml-closed-car" aria-hidden="true">▸</span>
          <span className="ml-eyebrow">Closed deals</span>
        </button>
        <div className="ml-closed-tabs" role="tablist" aria-label="Closed deals">
          <button type="button" role="tab" aria-selected={tab === 'won'}
                  className={'ml-closed-tab is-won' + (tab === 'won' ? ' on' : '')}
                  onClick={() => pickTab('won')}>
            Won <b data-testid="closed-won-count">{data.won.length}</b>
          </button>
          <button type="button" role="tab" aria-selected={tab === 'lost'}
                  className={'ml-closed-tab is-lost' + (tab === 'lost' ? ' on' : '')}
                  onClick={() => pickTab('lost')}>
            Lost <b data-testid="closed-lost-count">{data.lost.length}</b>
          </button>
        </div>
        <div className="ml-closed-month">
          <button type="button" aria-label="Previous month" onClick={() => setCur(shiftMonth(cur, -1))}>‹</button>
          <b data-testid="closed-month">{monthTitle(cur)}</b>
          <button type="button" aria-label="Next month" onClick={() => setCur(shiftMonth(cur, 1))}>›</button>
        </div>
      </div>

      {open && (
        <div className="ml-closed-body" role="tabpanel">
          {error && <div className="ml-closed-error" role="alert">{error}</div>}
          {list.length === 0 ? (
            <div className="ml-closed-empty">No deals {tab} in {monthTitle(cur)}.</div>
          ) : (
            <table className="ml-closed-table">
              <thead>
                <tr>
                  <th>Client</th><th>Venue</th><th>Month</th><th className="n">Ticket</th>
                  <th>Closed</th><th>Details</th>{showReopen && <th><span className="ml-sc-sr">Action</span></th>}
                </tr>
              </thead>
              <tbody>
                {list.map(d => (
                  <tr key={d.id} data-testid={`closed-${tab}-${d.id}`}>
                    <td className="cl">{d.client_name}</td>
                    <td className="mu">{(d.venues || []).map(venueLabel).join(', ') || '—'}</td>
                    <td className="mu">{d.event_month || '—'}</td>
                    <td className="n">{money(d.ticket_size)}</td>
                    <td className="mu">{weekLabel(d.closed_on)}</td>
                    <td className="mu">{d.deal_details || ''}</td>
                    {showReopen && (
                      <td className="act">
                        {openNow.has(norm(d.client_name))
                          ? <span className="ml-closed-note">on this week's table</span>
                          : <button type="button" className="md-btn ml-closed-reopen"
                                    disabled={busy === d.id} onClick={() => reopen(d)}>
                              {busy === d.id ? 'Reopening…' : 'Reopen'}
                            </button>}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <div className="ml-closed-foot">
            {tab === 'won'
              ? 'Won deals stay on the table the week they close, and are listed here for the month. The scorecard is unchanged.'
              : editable
                ? "Reopen copies the deal onto this week's table as an open Quotation."
                : 'Lost deals leave the table the week after they are marked. Unlock the card to reopen one.'}
          </div>
        </div>
      )}
    </div>
  );
}
